'use client';
import React, { useMemo, useState } from 'react';

import { Box, Button, Center, Checkbox, Flex, Show, Text, useColorMode } from '@chakra-ui/react';
import { useExchangeRateQuery } from '@stader-labs/web-sdk';
import BigNumber from 'bignumber.js';
import { usePathname } from 'next/navigation';

import { ContractTokenConfig } from '@/config/contracts.config';
import { SD, STAKE_CONTRACTS_MAPPING } from '@/constants/constants';
import { defiCurrencyIconMapping } from '@/constants/dappWalletMapping';
import { useChainData } from '@/providers/ChainDataProvider';
import { getCurrentChain, toFixedWithoutRounding } from '@/utils/common';

import Icon from '../Icon';
import { CloseIcon } from '../CloseIcon';
import { ModalDrawer } from '../ModalDrawer';

interface StakeConfirmationModalProps {
  isOpen: boolean;
  closeAlert: () => void;
  onSubmitPrimary: () => void;
  actionType: 'stake' | 'approve' | 'delegate';
  amount: string;
  stakeToken: ContractTokenConfig | null;
  primaryBtnTxt?: string;
}

const token = process.env.NEXT_PUBLIC_TOKEN || "";

const StakeConfirmationModal = ({
  isOpen,
  closeAlert,
  onSubmitPrimary,
  actionType,
  amount,
  stakeToken,
}: StakeConfirmationModalProps) => {
  const pathname = usePathname();
  const dappName = getCurrentChain(pathname);
  const { contractConfig, chainId, chain } = useChainData();

  const { colorMode } = useColorMode();
  const [isChecked, setIsChecked] = useState(false);

  const { data: exchangeRate } = useExchangeRateQuery({
    enabled: dappName !== SD,
  });

  const amountBN = useMemo(() => {
    return BigNumber(amount || 0);
  }, [amount]);

  const exchangeAmount = useMemo(() => {
    if (amountBN.isZero() || exchangeRate == null) {
      return BigNumber(0);
    }

    // For approve, stake, and delegate, calculate what they'll receive
    if (actionType === 'stake' || actionType === 'delegate' || actionType === 'approve') {
      return amountBN.dividedBy(exchangeRate);
    }

    return amountBN.multipliedBy(exchangeRate);
  }, [amountBN, exchangeRate, actionType]);

  const [inToken, outToken] = useMemo(() => {
    // For approve, we still want to show what they're approving (stakeToken) 
    // and what they'll receive after staking (xToken) as a preview
    if (actionType === 'stake' || actionType === 'delegate' || actionType === 'approve') {
      return [stakeToken, contractConfig?.xToken];
    }
    return [contractConfig?.xToken, stakeToken];
  }, [contractConfig?.xToken, stakeToken, actionType]);

  const getModalTitle = () => {
    if (actionType === 'approve') {
      return 'Review Approve';
    }
    const actionTitle = dappName === SD ? 'Delegate' : 'Stake';
    return `Review ${actionTitle}`;
  };



  const renderTokenSection = (
    tokenSymbol: string | undefined,
    amount: BigNumber,
    actionText: string,
  ) => (
    <Flex
      direction="column"
      alignItems={{ base: 'flex-start', md: 'center' }}
      flex={1}
      gap="6px"
    >
      <Text fontSize="xs" color="textSecondary">
        You {actionText}
      </Text>
      <Flex gap="6px" alignItems="center" flex={1}>
        {tokenSymbol &&
          defiCurrencyIconMapping[tokenSymbol.toLowerCase()] && (
            <Icon
              width="32px"
              height="32px"
              Icon={
                defiCurrencyIconMapping[tokenSymbol.toLowerCase()][colorMode]
              }
            />
          )}
        <Text fontSize="1.5rem" fontWeight="600">
          {toFixedWithoutRounding(amount, 4)} {tokenSymbol}
        </Text>
      </Flex>
    </Flex>
  );

  const tokenKey = useMemo(() => {
    return token === "POLYGON" && chainId ? `${token}_${chainId}` : token;
  }, [token, chainId]);

  const shouldShowReceive = useMemo(() => {
    return (
      (actionType === 'stake' || actionType === 'delegate' || actionType === 'approve') &&
      dappName !== SD &&
      exchangeRate != null &&
      !exchangeAmount.isZero()
    );
  }, [actionType, dappName, exchangeRate, exchangeAmount]);

  return (
    <ModalDrawer isOpen={isOpen} closeAlert={closeAlert}>
      <Center
        padding={{ base: '1rem 1rem 2.5rem', md: '2rem' }}
        fontWeight="600"
        flexDirection="column"
        width="100%"
        position="relative"
      >
        <Show above="md">
          <Box position="absolute" top={8} right={8}>
            <CloseIcon onClose={closeAlert} />
          </Box>
        </Show>
        <Flex flexDir="column" gap={8} width="100%" alignItems="start">
          <Text as="h3" fontSize="1.5rem">
            {getModalTitle()}
          </Text>

          <Flex
            width="100%"
            bgColor="backgroundPrimary"
            borderRadius={8}
            px={6}
            py={4}
            justifyContent="space-between"
            gap={4}
            direction={{ base: 'column', md: 'row' }}
          >
            {/* Send Section */}
            {actionType === 'delegate' && dappName === SD ? (
              // For SD delegation - only show delegate section
              renderTokenSection(inToken?.symbol, amountBN, 'delegate')
            ) : (
              // For stake and approve - show send and receive sections
              <>
                {renderTokenSection(
                  inToken?.symbol,
                  amountBN,
                  actionType === 'approve' ? 'approve' : 'send',
                )}
                {/* Receive Section - Show for stake/approve when exchange rate is available */}
                {shouldShowReceive &&
                  renderTokenSection(
                    outToken?.symbol,
                    exchangeAmount,
                    'receive',
                  )}
              </>
            )}
          </Flex>

          {token && STAKE_CONTRACTS_MAPPING[tokenKey as keyof typeof STAKE_CONTRACTS_MAPPING] && (
            <Box
              bg="#FFF4E6"
              borderRadius="12px"
              padding="12px 16px"
              marginTop="12px"
              width="100%"
              border="1px solid #FFE5B4"
            >
              <Flex gap="8px" alignItems="flex-start">
                <Checkbox
                  isChecked={isChecked}
                  onChange={(e) => setIsChecked(e.target.checked)}
                  colorScheme="green"
                  size="lg"
                  bg="#FFF"
                />

                <Flex
                  gap="8px"
                  flexDirection="column"
                  alignItems="flex-start"
                  flex="1"
                >
                  <Text
                    fontSize="12px"
                    lineHeight="16px"
                    fontWeight={400}
                    color="#4A6A6A"
                  >
                    I will verify my wallet interacts only with the contract
                  </Text>
                  <Text
                    fontSize="12px"
                    lineHeight="16px"
                    fontWeight={500}
                    color="#1A1A1A"
                    wordBreak="break-all"
                  >
                    {chain?.name} {STAKE_CONTRACTS_MAPPING[tokenKey as keyof typeof STAKE_CONTRACTS_MAPPING]?.address}
                  </Text>
                </Flex>
              </Flex>
            </Box>
          )}

          <Flex gap="16px" width="100%" direction={{ base: 'column', md: 'row' }}>

            <Button
              bg="primary"
              color="white"
              minHeight="56px"
              fontWeight="600"
              fontSize="16px"
              borderRadius="12px"
              flex={1}
              onClick={onSubmitPrimary}
              _hover={{
                bg: 'primary',
                opacity: 0.9,
              }}
              isDisabled={
                token &&
                  STAKE_CONTRACTS_MAPPING[tokenKey as keyof typeof STAKE_CONTRACTS_MAPPING]
                  ? !isChecked
                  : false
              }
            >
              Confirm
            </Button>
          </Flex>
        </Flex>
      </Center>
    </ModalDrawer>
  );
};

export default StakeConfirmationModal;

